import React, { useMemo, useState } from "react";
import { Alert, Box } from "@mui/material";
import {
  Bar, BarChart, CartesianGrid, ComposedChart, LabelList, Line,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";

import {
  getSpammerSummary, getSpammerTrend, getSpammerDetail,
} from "../../services/ops/opsservices";
import { useClientTimeZone, zoneAbbr } from "../../utils/clienttimezone";
import ConversationDialog from "./ConversationDialog";
import {
  BLUE, GREEN, AMBER, RED, NAVY,
  CounterCard, ChartCard, SectionTitle, Caption, DetailTable, LoadingRow,
  OpsSlicers, StatusChip, identityColumns, drillColumn, useOpsData,
  DEFAULT_GRAN, DEFAULT_RANGE, getDateRange,
  formatRangeLabel,
  fmtInt, fmtPct, fmtAxisDate, fmtTime, fmtDuration, safeNum,
} from "./opsShared";

// Must match SPAM_REPEAT_THRESHOLD in opsRoutes.js. Duplicated rather than
// fetched because it appears in labels, and a tile reading "5+" while the
// server counted 8 is worse than a constant in two places.
const REPEAT_THRESHOLD = 5;

// A loop that has run past this is no longer a customer being repetitive.
// 306 turns was the WENDSON JOACHIN thread; 50 is well clear of any human
// conversation and still catches a loop early.
const RUNAWAY_TURNS = 50;

export default function SpammerDashboard() {
  const [gran,  setGran]  = useState(DEFAULT_GRAN);
  const [range, setRange] = useState(DEFAULT_RANGE);

  const { timeZone, tzReady } = useClientTimeZone();
  const tzLabel = zoneAbbr(timeZone);

  const params = useMemo(() => ({
    ...getDateRange(range, timeZone),
    granularity: gran.toLowerCase(),
  }), [range, gran, timeZone]);

  const [drill, setDrill] = useState(null);
  const openThread = (row) => setDrill(row);

  const { data, loading, error } = useOpsData({
    summary: getSpammerSummary,
    trend:   getSpammerTrend,
    detail:  getSpammerDetail,
  }, params, tzReady);

  const s      = data.summary || {};
  const detail = data.detail || [];

  const spammers   = safeNum(s.spammers);
  const spamMsgs   = safeNum(s.spamMessages);
  const burned     = safeNum(s.repliesBurned);
  const worst      = safeNum(s.worstRepeats);
  const spamRate   = safeNum(s.spamRate);

  // A loop that produced a payment event was handing out live payment links on
  // every turn, so it is the subset worth opening first.
  const withPayment = detail.filter((d) => safeNum(d.involvedPayment) === 1);
  const runaway     = detail.filter((d) => safeNum(d.conversationTurns) >= RUNAWAY_TURNS);

  const trend = (data.trend || []).map((r) => ({
    date:     fmtAxisDate(r.summary_date, gran, timeZone),
    messages: safeNum(r.messagesReceived),
    spam:     safeNum(r.spamMessages),
    spammers: safeNum(r.spammers),
    rate:     safeNum(r.spamRate),
  }));

  // Top offenders, built from the detail rows already on the page rather than
  // a fourth endpoint — the detail comes back ordered by repeats, so this is
  // just the head of it.
  const top = detail.slice(0, 10).map((d) => ({
    name: `${d.firstName || ""} ${d.lastName || ""}`.trim() || d.accountId || "—",
    repeats: safeNum(d.repeats),
    replies: safeNum(d.repliesSent),
  }));

  const rangeLabel = formatRangeLabel(params, timeZone);
  const titleOf = (t) => `${t} · ${rangeLabel}`;

  const columns = [
    drillColumn(openThread),
    ...identityColumns,
    { key: "repeats", label: "Times Sent", width: 100, align: "right",
      render: (r) => <StatusChip label={String(safeNum(r.repeats))} tone="bad" /> },
    { key: "repliesSent", label: "Replies Sent", width: 110, align: "right",
      render: (r) => safeNum(r.repliesSent) },
    { key: "conversationTurns", label: "Thread Length", width: 120, align: "right",
      render: (r) => {
        const n = safeNum(r.conversationTurns);
        return n >= RUNAWAY_TURNS
          ? <StatusChip label={`${n} turns`} tone="bad" />
          : `${n} turns`;
      } },
    { key: "involvedPayment", label: "Payment", width: 110, align: "center",
      render: (r) => (
        safeNum(r.involvedPayment) === 1
          ? <StatusChip label="Live links" tone="bad" />
          : <StatusChip label="No" tone="default" />
      ) },
    { key: "minutesSpan", label: "Over", width: 90, align: "right",
      render: (r) => fmtDuration(safeNum(r.minutesSpan) * 60) },
    { key: "firstAtUtc", label: "First", width: 160, render: (r) => fmtTime(r.firstAtUtc, timeZone) },
    { key: "lastAtUtc",  label: "Last",  width: 160, render: (r) => fmtTime(r.lastAtUtc, timeZone) },
    { key: "sampleMessage", label: "The Repeated Message", width: 300 },
    { key: "sampleReply",   label: "What AI Kept Replying", width: 300 },
  ];

  return (
    <Box sx={{ p: 2 }}>
      <OpsSlicers gran={gran} setGran={setGran} range={range} setRange={setRange} tzLabel={tzLabel} />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <LoadingRow text="Looking for repeated messages…" />}

      {!loading && (
        <>
          <SectionTitle>Spammer Watch</SectionTitle>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0,1fr))", gap: 1, mb: 1.5 }}>
            <CounterCard label="Spammers" value={fmtInt(spammers)}
              color={GREEN} status={spammers === 0 ? "ok" : "bad"} big
              sub={`Same message ${REPEAT_THRESHOLD}+ times`} />
            <CounterCard label="Repeated Messages" value={fmtInt(spamMsgs)}
              color={GREEN} status={spamMsgs === 0 ? "ok" : "warn"} big
              sub={`${fmtPct(spamRate)} of all inbound`} />
            <CounterCard label="Replies Burned" value={fmtInt(burned)}
              color={GREEN} status={burned === 0 ? "ok" : "bad"} big
              sub="AI calls spent on a repeat" />
            <CounterCard label="Worst Offender" value={worst ? `${fmtInt(worst)}×` : "—"}
              color={AMBER} sub="Same text, one conversation" />
            <CounterCard label="Runaway Threads" value={fmtInt(runaway.length)}
              color={GREEN} status={runaway.length === 0 ? "ok" : "bad"} big
              sub={`Over ${RUNAWAY_TURNS} turns`} />
          </Box>

          {withPayment.length > 0 && (
            <Alert severity="error" sx={{ mb: 2, fontSize: 13 }}>
              {withPayment.length}{" "}
              {withPayment.length === 1 ? "loop is" : "loops are"} on an account with a
              processed payment. Every turn in a PayNearMe loop issues a new live payment
              link, so the same account may be holding several. Open the row and check the
              ledger before assuming one charge.
            </Alert>
          )}

          {runaway.length > 0 && withPayment.length === 0 && (
            <Alert severity="warning" sx={{ mb: 2, fontSize: 13 }}>
              {runaway.length}{" "}
              {runaway.length === 1 ? "conversation has" : "conversations have"} run past{" "}
              {RUNAWAY_TURNS} turns on a repeating message. That shape is an auto-responder
              on the other end answering AI, not a customer — nothing else on Ops flags it,
              because every one of those turns was answered in seconds.
            </Alert>
          )}

          <Box sx={{ mb: 1.5 }}>
            <ChartCard title={titleOf("Repeated messages against all inbound")}
              right={`${fmtPct(spamRate)} of volume`} height={280}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={trend} margin={{ top: 18, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#888" }}
                    interval="preserveStartEnd" minTickGap={24} />
                  <YAxis yAxisId="left" tick={{ fontSize: 10, fill: "#888" }} allowDecimals={false} />
                  <YAxis yAxisId="right" orientation="right"
                    tick={{ fontSize: 10, fill: RED }} tickFormatter={(v) => `${v}%`} />
                  <Tooltip contentStyle={{ fontSize: 12 }} />
                  {/* Total volume behind, so a spike in repeats reads against
                      how much traffic it came out of — a loop on a quiet day
                      is a much larger share than the raw count suggests. */}
                  <Bar yAxisId="left" dataKey="messages" name="Messages received"
                    fill={`${NAVY}22`} radius={[3, 3, 0, 0]} />
                  <Bar yAxisId="left" dataKey="spam" name="Repeated messages"
                    fill={RED} radius={[3, 3, 0, 0]}>
                    <LabelList dataKey="spam" position="top" fontSize={10} fill={RED}
                      formatter={(v) => (v > 0 ? v : "")} />
                  </Bar>
                  <Line yAxisId="right" type="monotone" dataKey="rate" name="Share of inbound"
                    stroke={RED} strokeWidth={2} dot={{ r: 3 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </ChartCard>
            <Caption>
              Counted by identical text on one conversation, not by volume. A customer
              sending twenty different messages is having a conversation; one sending the
              same message {REPEAT_THRESHOLD} times is a keyword masher or, more often, an
              auto-responder that AI is answering in a loop.
            </Caption>
          </Box>

          {top.length > 0 && (
            <Box sx={{ mb: 1.5 }}>
              <ChartCard title={titleOf("Worst offenders")} height={Math.max(220, top.length * 34)}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={top} layout="vertical"
                    margin={{ top: 8, right: 40, left: 8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 10, fill: "#888" }} allowDecimals={false} />
                    <YAxis type="category" dataKey="name" width={150}
                      tick={{ fontSize: 10, fill: "#888" }} />
                    <Tooltip contentStyle={{ fontSize: 12 }} />
                    {/* Replies sits beside repeats rather than behind it: the
                        gap between the two is the only thing that separates a
                        loop AI is feeding from one it stopped answering. */}
                    <Bar dataKey="repeats" name="Times sent" fill={RED} radius={[0, 3, 3, 0]}>
                      <LabelList dataKey="repeats" position="right" fontSize={10} fill={RED} />
                    </Bar>
                    <Bar dataKey="replies" name="Replies sent" fill={BLUE} radius={[0, 3, 3, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </Box>
          )}

          <SectionTitle right={`${detail.length} shown · most repeats first`}>
            Repeated Messages
          </SectionTitle>
          <DetailTable
            columns={columns}
            rows={detail}
            emptyText={`Nobody sent the same message ${REPEAT_THRESHOLD} times in this range.`}
            maxHeight={480}
          />
          <Caption>
            One row per repeated phrase, so a thread bouncing between two auto-replies shows
            twice. Replies Sent against Times Sent is the cost: each reply is a PromptFlow
            call and an outbound segment spent answering something already answered.
          </Caption>
        </>
      )}

      <ConversationDialog
        open={!!drill}
        onClose={() => setDrill(null)}
        customerdataid={drill?.customerdataid}
        subtitle={drill?.repeats ? `${drill.repeats} identical messages` : null}
      />
    </Box>
  );
}